import '../styles/globals.css';
import dynamic from 'next/dynamic';
import App from 'next/app';
import FJLocalStore from '../src/store/FJLocalStore';
import { ModalProvider } from '../src/context/AppDownloadModal';

// 动态导入 AppDownloadModal，仅在客户端加载，减少首屏 JS 体积
const AppDownloadModal = dynamic(
  () => import('../components/AppDownloadModal/AppDownloadModal'),
  { ssr: false }
);

// 语言标签到文件名的映射
const LANG_FILE_MAP = {
  cn: 'cn',
  de: 'de',
  en: 'en',
  es: 'es',
  fr: 'fr',
  jp: 'jp',
  pt: 'pt',
  tw: 'tw',
  it: 'it',
  id: 'id',
  ar: 'ar',
};

// 服务端加载语言包 - 通过 next.config.js 配置 webpack，客户端会忽略 fs/path
async function loadLanguageOnServer(locale) {
  // 仅在服务端执行
  if (typeof window !== 'undefined') {
    return { tag: locale || 'en', langData: null };
  }

  // 服务端使用 Node.js 原生模块读取文件
  // 客户端构建时，fs 和 path 通过 webpack 配置被设置为 false
  const fs = require('fs');
  const path = require('path');
  const tag = LANG_FILE_MAP[locale] ? locale : 'en';

  try {
    const filePath = path.join(process.cwd(), 'public', 'language', `local.${LANG_FILE_MAP[tag]}.json`);
    const fileContent = fs.readFileSync(filePath, 'utf-8');
    const langData = JSON.parse(fileContent);
    return { tag, langData };
  } catch (e) {
    console.error(`Failed to load language: ${tag}`, e);
    // 降级到英文
    try {
      const fallbackPath = path.join(process.cwd(), 'public', 'language', 'local.en.json');
      const fallbackContent = fs.readFileSync(fallbackPath, 'utf-8');
      const langData = JSON.parse(fallbackContent);
      return { tag: 'en', langData };
    } catch (fallbackError) {
      console.error('Failed to load fallback English language', fallbackError);
      return { tag: 'en', langData: {} };
    }
  }
}

function MyApp({ Component, pageProps, langData, language }) {
  // pageProps.langData 用于语言来自接口返回的页面（如 ssgRender），优先级高于 _app.getInitialProps 加载的
  const effectiveLangData = pageProps.langData || langData;
  const effectiveLanguage = pageProps.langData ? pageProps.language : language;

  if (effectiveLangData) {
    FJLocalStore.injectLanguage(effectiveLanguage, effectiveLangData);
  }
  FJLocalStore.setCurrentTag(effectiveLanguage);

  return (
    <ModalProvider>
      <Component {...pageProps} />
      <AppDownloadModal />
    </ModalProvider>
  );
}

// 在服务端预加载语言包
MyApp.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext);
  // ctx.locale 仅对有 URL 路径前缀（如 /jp/...）的页面有效
  // 对于通过 query 参数传递语言的页面（如 /ssrPreview?page_language=jp），优先读 query 里的标签
  const locale = appContext.ctx.query?.page_language || appContext.ctx.query?.lang || appContext.ctx.locale || 'en';

  // ssgRender的本地化由接口返回，自行加载 langData，此处跳过避免 __NEXT_DATA__ 中存在两份语言包影响 LCP
  const pathname = appContext.ctx.pathname || '';
  if (pathname.includes('/ssgRender')) {
    return { ...appProps, langData: null, language: locale };
  }

  // 服务端加载语言数据
  const { tag, langData } = await loadLanguageOnServer(locale);

  return {
    ...appProps,
    langData,
    language: tag,
  };
};

export function reportWebVitals(metric) {
  if (process.env.NEXT_PUBLIC_SERVER_TAG_NODE === 'release') {
    return;
  }

  const { name, value, id } = metric;

  const formatValue = (name, value) => {
    if (name === 'CLS') return value.toFixed(3);
    return `${Math.round(value)}ms`;
  };

  const getLevel = (name, value) => {
    const thresholds = {
      FCP: [1800, 3000],
      LCP: [2500, 4000],
      FID: [100, 300],
      CLS: [0.1, 0.25],
      TTFB: [800, 1800]
    };

    const [good, poor] = thresholds[name] || [];
    if (!good) return '🤔';// next相关几个指标暂不处理
    return value <= good ? '✅' : value <= poor ? '⚠️' : '❌';
  };

  switch (name) {
    case 'FCP':
      console.log(`%c[FCP] 首次内容绘制: ${formatValue(name, value)} ${getLevel(name, value)}`,
        'color: #FFD700; font-weight: bold;');
      break;

    case 'LCP':
      console.log(`%c[LCP] 最大内容绘制: ${formatValue(name, value)} ${getLevel(name, value)}`,
        'color: #40E0D0; font-weight: bold;');
      break;

    case 'CLS':
      console.log(`%c[CLS] 累积布局偏移: ${formatValue(name, value)} ${getLevel(name, value)}`,
        'color: #4169E1; font-weight: bold;');
      break;

    case 'FID':
      console.log(`%c[FID] 首次输入延迟: ${formatValue(name, value)} ${getLevel(name, value)}`,
        'color: #2E8B57; font-weight: bold;');
      break;

    case 'TTFB':
      console.log(`%c[TTFB] 首字节时间: ${formatValue(name, value)} ${getLevel(name, value)}`,
        'color: #9370DB; font-weight: bold;');
      break;

    case 'Next.js-hydration':
      console.log(`%c[Hydration] 页面水合时间: ${formatValue(name, value)}`,
        'color: #87CEEB; font-weight: bold;');
      break;

    case 'Next.js-route-change-to-render':
      console.log(`%c[Route] 路由切换到渲染: ${formatValue(name, value)}`,
        'color: #DDA0DD; font-weight: bold;');
      break;

    case 'Next.js-render':
      console.log(`%c[Render] 页面渲染时间: ${formatValue(name, value)}`,
        'color: #98FB98; font-weight: bold;');
      break;
  }

  // 性能警告
  if (getLevel(name, value) === '❌') {
    console.warn(
      '%c性能警告 ⚡️%c',
      'background: #ff0000; color: white; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
      'color: inherit;',
      {
        page: window.location.pathname,
        metric: name,
        value: formatValue(name, value),
        id
      }
    );
  }
}

export default MyApp;
